/**
 * RoleContractDetailPage — J7-W2 task 2
 *
 * Read-only view of a Role Contract showing all 8 GOV-06 components in
 * collapsible sections. Header has Edit / Activate / Archive buttons that
 * call the existing endpoints. Activation surfaces the GOV-06 validation
 * error inline if any component is missing.
 */

import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Alert } from '@/components/ui/Alert';
import { showToast } from '@/components/ui/Toast';
import { ScrollText, Pencil, CheckCircle2, Archive } from 'lucide-react';
import { t } from '@/lib/i18n';
import {
  useRorosRoleContract,
  useRorosRoleContractActivate,
  useRorosRoleContractArchive,
} from '@/hooks/roros/role-contracts';
import type { RorosRoleContractDTO } from '@/services/roros/governance.service';

function Section({ title, children, isEmpty }: { title: string; children: React.ReactNode; isEmpty: boolean }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          {isEmpty ? (
            <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">
              {t('Empty')}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">
              {t('Configured')}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <p className="text-sm text-muted-foreground italic">{t('Not configured yet.')}</p>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

// ─── Structured field renderers (A.3) — replace JSON.stringify blobs ─────────

function ToolAccessPolicyView({ tap }: { tap: RorosRoleContractDTO['toolAccessPolicy'] }) {
  const policy = Array.isArray(tap) ? {} : (tap as { grants?: Array<{ tool: string; accessLevel: string }>; budget_cap_usd?: number | null });
  const grants = Array.isArray(policy.grants) ? policy.grants : Array.isArray(tap) ? (tap as Array<{ tool: string; accessLevel: string }>) : [];
  const cap = policy.budget_cap_usd;
  return (
    <div className="space-y-3 text-sm">
      {cap !== null && cap !== undefined && (
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('Monthly budget cap')}:</span>
          <span className="font-medium">${Number(cap).toFixed(2)}</span>
        </div>
      )}
      {grants.length > 0 ? (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">{t('Tool grants')}</p>
          <ul className="space-y-1">
            {grants.map((g, i) => (
              <li key={i} className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{g.accessLevel}</Badge>
                <span>{g.tool}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-muted-foreground italic text-xs">{t('No tool grants configured.')}</p>
      )}
    </div>
  );
}

function QcDutiesView({ qc }: { qc: Record<string, unknown> }) {
  const canPerform = Boolean(qc.canPerformQc);
  const types: string[] = Array.isArray(qc.qcTypes) ? (qc.qcTypes as string[]) : [];
  const checklist: string[] = Array.isArray(qc.qualityChecklist)
    ? (qc.qualityChecklist as string[])
    : typeof qc.qualityChecklist === 'string' && qc.qualityChecklist
      ? [qc.qualityChecklist]
      : [];
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">{t('Can perform QC')}:</span>
        <Badge variant={canPerform ? 'success' : 'secondary'}>{canPerform ? t('Yes') : t('No')}</Badge>
      </div>
      {types.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-muted-foreground">{t('QC types')}:</span>
          {types.map((tp) => <Badge key={tp} variant="outline" className="text-xs">{tp}</Badge>)}
        </div>
      )}
      {checklist.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">{t('Quality checklist')}</p>
          <ul className="space-y-0.5 list-disc list-inside">
            {checklist.map((item, i) => <li key={i} className="text-xs">{item}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function InterfacesView({ ifaces }: { ifaces: Record<string, unknown> }) {
  const reportingTo: string[] = Array.isArray(ifaces.reportingTo) ? (ifaces.reportingTo as string[]) : [];
  const collaboratesWith: string[] = Array.isArray(ifaces.collaboratesWith) ? (ifaces.collaboratesWith as string[]) : [];
  const escalatesTo: string[] = Array.isArray(ifaces.escalatesTo) ? (ifaces.escalatesTo as string[]) : [];
  const rows = [
    { label: t('Reports to'), ids: reportingTo },
    { label: t('Collaborates with'), ids: collaboratesWith },
    { label: t('Escalates to'), ids: escalatesTo },
  ].filter((r) => r.ids.length > 0);
  return (
    <div className="space-y-2 text-sm">
      {rows.map((r) => (
        <div key={r.label} className="flex items-start gap-2 flex-wrap">
          <span className="text-muted-foreground shrink-0">{r.label}:</span>
          <div className="flex flex-wrap gap-1">
            {r.ids.map((id) => <Badge key={id} variant="outline" className="text-xs font-mono">{id}</Badge>)}
          </div>
        </div>
      ))}
    </div>
  );
}

function UpgradeRulesView({ up }: { up: Record<string, unknown> }) {
  const autoSuggest: string[] = Array.isArray(up.autoSuggestComponents) ? (up.autoSuggestComponents as string[]) : [];
  const ceoReview: string[] = Array.isArray(up.requiresCeoReview) ? (up.requiresCeoReview as string[]) : [];
  const maxPerMonth = up.maxAutoUpgradesPerMonth !== undefined && up.maxAutoUpgradesPerMonth !== null
    ? Number(up.maxAutoUpgradesPerMonth)
    : null;
  return (
    <div className="space-y-2 text-sm">
      {maxPerMonth !== null && (
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('Max auto-upgrades / month')}:</span>
          <span className="font-medium">{maxPerMonth}</span>
        </div>
      )}
      {autoSuggest.length > 0 && (
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-muted-foreground shrink-0">{t('Auto-suggest')}:</span>
          <div className="flex flex-wrap gap-1">{autoSuggest.map((k) => <Badge key={k} variant="outline" className="text-xs">{k}</Badge>)}</div>
        </div>
      )}
      {ceoReview.length > 0 && (
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-muted-foreground shrink-0">{t('CEO review required')}:</span>
          <div className="flex flex-wrap gap-1">{ceoReview.map((k) => <Badge key={k} variant="outline" className="text-xs">{k}</Badge>)}</div>
        </div>
      )}
    </div>
  );
}

function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim().length === 0;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v as Record<string, unknown>).length === 0;
  return false;
}

export function RoleContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const contractQuery = useRorosRoleContract(id ?? '');
  const activateMutation = useRorosRoleContractActivate();
  const archiveMutation = useRorosRoleContractArchive();

  const [showArchive, setShowArchive] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);

  const handleActivate = () => {
    if (!id) return;
    setActivateError(null);
    activateMutation.mutate(id, {
      onSuccess: () => showToast.success(t('Role Contract activated')),
      onError: (err) => {
        const msg = err instanceof Error ? err.message : t('Failed to activate Role Contract');
        setActivateError(msg);
        showToast.error(msg);
      },
    });
  };

  const handleArchive = () => {
    if (!id) return;
    archiveMutation.mutate(id, {
      onSuccess: () => {
        showToast.success(t('Role Contract archived'));
        setShowArchive(false);
        navigate('/roros/role-contracts');
      },
      onError: (err) => {
        showToast.error(err instanceof Error ? err.message : t('Failed to archive Role Contract'));
        setShowArchive(false);
      },
    });
  };

  return (
    <RorosPage
      title={t('Role Contract')}
      icon={<ScrollText className="h-5 w-5" />}
      breadcrumbs={[{ label: t('Role Contracts'), path: '/roros/role-contracts' }]}
    >
      <QueryPage query={contractQuery} emptyMessage={t('Role Contract not found')}>
        {(contract: RorosRoleContractDTO) => {
          const isDraft = contract.status === 'draft';
          const isArchived = contract.status === 'archived';
          return (
            <div className="space-y-6 mt-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-semibold">{contract.name}</h2>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge>{contract.status}</Badge>
                        <Badge variant="outline">v{contract.version}</Badge>
                        {contract.actorId && (
                          <Link
                            to={`/roros/actors/${contract.actorId}`}
                            className="text-xs text-primary-600 hover:underline"
                          >
                            {t('Linked actor →')}
                          </Link>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isDraft && (
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/roros/role-contracts/${contract.id}/edit`)}
                          data-testid="role-contract-edit-button"
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" />
                          {t('Edit')}
                        </Button>
                      )}
                      {isDraft && (
                        <Button
                          onClick={handleActivate}
                          disabled={activateMutation.isPending}
                          data-testid="role-contract-activate-button"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          {activateMutation.isPending ? t('Activating...') : t('Activate')}
                        </Button>
                      )}
                      {!isArchived && contract.status !== 'template' && (
                        <Button
                          variant="outline"
                          onClick={() => setShowArchive(true)}
                          data-testid="role-contract-archive-button"
                        >
                          <Archive className="h-3.5 w-3.5 mr-1" />
                          {t('Archive')}
                        </Button>
                      )}
                    </div>
                  </div>
                  {activateError && (
                    <Alert variant="error" className="mt-4">
                      {activateError}
                    </Alert>
                  )}
                </CardContent>
              </Card>

              <Section title={t('Purpose & Scope')} isEmpty={isEmpty(contract.purposeScope)}>
                <p className="text-sm whitespace-pre-line">{contract.purposeScope}</p>
              </Section>

              <Section title={t('Outcomes Ownership')} isEmpty={isEmpty(contract.outcomesOwnership)}>
                <ul className="space-y-1 text-sm">
                  {contract.outcomesOwnership.map((o, i) => (
                    <li key={`${o.metricId}-${i}`} className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{o.access}</Badge>
                      <span>{o.metricName ?? o.metricId}</span>
                    </li>
                  ))}
                </ul>
              </Section>

              <Section title={t('Routine Ownership')} isEmpty={isEmpty(contract.routineOwnership)}>
                <ul className="space-y-1 text-sm">
                  {contract.routineOwnership.map((r, i) => (
                    <li key={`${r.routineId}-${i}`}>{r.routineName ?? r.routineId}</li>
                  ))}
                </ul>
              </Section>

              <Section title={t('Decision Rights')} isEmpty={isEmpty(contract.decisionRights)}>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(contract.decisionRights).map(([level, action]) => (
                    <React.Fragment key={level}>
                      <dt className="text-muted-foreground capitalize">{level}</dt>
                      <dd className="font-medium">{String(action)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </Section>

              <Section title={t('Tool Access Policy')} isEmpty={isEmpty(contract.toolAccessPolicy)}>
                <ToolAccessPolicyView tap={contract.toolAccessPolicy} />
              </Section>

              <Section title={t('QC Duties')} isEmpty={isEmpty(contract.qcDuties)}>
                <QcDutiesView qc={contract.qcDuties} />
              </Section>

              <Section title={t('Interfaces')} isEmpty={isEmpty(contract.interfaces)}>
                <InterfacesView ifaces={contract.interfaces} />
              </Section>

              <Section title={t('Upgrade Rules')} isEmpty={isEmpty(contract.upgradeRules)}>
                <UpgradeRulesView up={contract.upgradeRules} />
              </Section>

              <ConfirmDialog
                isOpen={showArchive}
                onClose={() => setShowArchive(false)}
                onConfirm={handleArchive}
                title={t('Archive this Role Contract?')}
                description={t('Archiving will deactivate the contract; the linked actor will lose its GOV-06 binding.')}
                confirmText={t('Archive')}
                variant="destructive"
                isLoading={archiveMutation.isPending}
              />
            </div>
          );
        }}
      </QueryPage>
    </RorosPage>
  );
}
